import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useStudy } from '../context/StudyContext';
import { apiService } from '../services/api';

const Progress = () => {
  const { topics, isLoading } = useStudy();
  const [progressData, setProgressData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedTopic, setSelectedTopic] = useState('all');
  const navigate = useNavigate();

  useEffect(() => {
    fetchProgressData();
  }, [selectedTopic]);

  const fetchProgressData = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiService.getProgress(selectedTopic);
      console.log('Progress data received:', data);
      setProgressData(data);
    } catch (error) {
      console.error('Error fetching progress data:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleContinueStudying = () => {
    if (selectedTopic !== 'all') {
      const topic = topics.find(t => t.api_topic === selectedTopic);
      if (topic) {
        navigate('/custom-study', { 
          state: { 
            selectedTopics: [topic.id] 
          } 
        });
        return;
      }
    }
    
    navigate('/custom-study');
  };

  const handleFocusWeakAreas = () => {
    const weakTopics = getWeakTopics();
    
    navigate('/custom-study', { 
      state: { 
        selectedTopics: weakTopics.length > 0 ? weakTopics : undefined,
        masteryFilters: ['needsWork', 'almostThere']
      } 
    });
  };

  const getWeakTopics = () => {
    if (!progressData?.topic_breakdown) return [];
    
    const weakTopicIds = [];
    
    Object.entries(progressData.topic_breakdown).forEach(([topicName, topicData]) => {
      const topic = topics.find(t => t.api_topic === topicName);
      if (topic && topicData.mastery_percentage < 70) {
        weakTopicIds.push(topic.id);
      }
    });
    
    return weakTopicIds;
  };

  const formatPercentage = (value) => {
    if (typeof value === 'number') {
      return value.toFixed(2);
    }
    return '0.00';
  };

  const getMasteryColor = (percentage) => {
    if (percentage >= 80) return '#10b981';
    if (percentage >= 60) return '#f59e0b';
    return '#ef4444';
  };

  const getCompletionColor = (percentage) => {
    if (percentage >= 80) return '#10b981';
    if (percentage >= 50) return '#f59e0b';
    return '#ef4444';
  };

  const calculateMasteryPercentage = () => {
    if (!progressData?.mastery_breakdown) return 0;
    const { mastered, almost_there, needs_work } = progressData.mastery_breakdown;
    const totalAnswered = mastered + almost_there + needs_work;
    return totalAnswered > 0 ? (mastered / totalAnswered) * 100 : 0;
  };

  const getCompletionPercentage = () => {
    return progressData?.completion_percentage || 0;
  };

  const getMasteryPercentage = () => {
    return progressData?.mastery_percentage || calculateMasteryPercentage();
  };

  const getMasteryCategoryPercentage = (count) => {
    if (!progressData?.answered || progressData.answered === 0) return 0;
    return (count / progressData.answered) * 100;
  };

  if (isLoading || loading) {
    return (
      <div style={styles.container}>
        <link href="https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap" rel="stylesheet" />
        <link href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.0.0/css/all.min.css" rel="stylesheet" />
        
        <div style={styles.header}>
          <div style={styles.headerContent}>
            <div style={styles.titleSection}>
              <h1 style={styles.title}>Learning Progress</h1>
              <p style={styles.subtitle}>Tracking your study journey and mastery</p>
            </div>
          </div>
        </div>

        <div style={styles.loadingContainer}>
          <div style={styles.spinner}></div>
          <p style={styles.loadingText}>Loading your progress data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.container}>
        <link href="https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap" rel="stylesheet" />
        <link href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.0.0/css/all.min.css" rel="stylesheet" />
        
        <div style={styles.header}>
          <div style={styles.headerContent}>
            <div style={styles.titleSection}>
              <h1 style={styles.title}>Learning Progress</h1>
              <p style={styles.subtitle}>Tracking your study journey and mastery</p>
            </div>
          </div>
        </div>

        <div style={styles.errorContainer}>
          <div style={styles.errorIcon}>
            <i className="fas fa-exclamation-triangle" style={{fontSize: '48px', color: '#ef4444'}}></i>
          </div>
          <h3 style={styles.errorTitle}>Unable to Load Progress</h3>
          <p style={styles.errorText}>{error}</p>
          <button style={styles.retryButton} onClick={fetchProgressData}>
            <i className="fas fa-redo" style={{marginRight: '8px'}}></i>
            Try Again
          </button>
        </div>
      </div>
    );
  }

  const completionPercentage = getCompletionPercentage();
  const masteryPercentage = getMasteryPercentage();

  return (
    <div style={styles.container}>
      <link href="https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap" rel="stylesheet" />
      <link href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.0.0/css/all.min.css" rel="stylesheet" />
      
      <div style={styles.header}>
        <div style={styles.headerContent}>
          <div style={styles.titleSection}>
            <h1 style={styles.title}>Learning Progress</h1>
            <p style={styles.subtitle}>Tracking your study journey and mastery</p>
          </div>
          
          <div style={styles.topicSelector}>
            <label htmlFor="topic-select" style={styles.selectorLabel}>
              <i className="fas fa-filter" style={{marginRight: '8px'}}></i>
              Filter by Topic:
            </label>
            <select 
              id="topic-select"
              value={selectedTopic}
              onChange={(e) => setSelectedTopic(e.target.value)}
              style={styles.selector}
            >
              <option value="all">All Topics</option>
              {topics.map(topic => (
                <option key={topic.id} value={topic.api_topic}>
                  {topic.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div style={styles.divider}></div>

      <div style={styles.content}>
        {progressData && (
          <>
            {/* Overview Cards */}
            <div style={styles.overviewGrid}>
              <div style={styles.overviewCard}>
                <div style={styles.cardHeader}>
                  <i className="fas fa-chart-line" style={styles.cardIcon}></i>
                  <h3 style={styles.cardTitle}>Overall Progress</h3>
                </div>
                <div style={styles.cardContent}>
                  <div style={styles.progressCircle}>
                    <div style={{
                      ...styles.progressRing,
                      background: `conic-gradient(#4f46e5 ${completionPercentage * 3.6}deg, #e5e7eb 0deg)`
                    }}>
                      <div style={styles.progressInner}>
                        <div style={styles.progressPercentage}>{formatPercentage(completionPercentage)}%</div>
                        <div style={styles.progressLabel}>Completed</div>
                      </div>
                    </div>
                  </div>
                  <div style={styles.progressStats}>
                    <div style={styles.statItem}>
                      <i className="fas fa-check-circle" style={{color: '#10b981'}}></i>
                      <span style={styles.statText}>{progressData.answered} Answered</span>
                    </div>
                    <div style={styles.statItem}>
                      <i className="fas fa-circle" style={{color: '#6b7280'}}></i>
                      <span style={styles.statText}>{progressData.unanswered} Remaining</span>
                    </div>
                    <div style={styles.statItem}>
                      <i className="fas fa-database" style={{color: '#4f46e5'}}></i>
                      <span style={styles.statText}>{progressData.total_questions} Total</span>
                    </div>
                  </div>
                </div>
              </div>

              <div style={styles.overviewCard}>
                <div style={styles.cardHeader}>
                  <i className="fas fa-trophy" style={styles.cardIcon}></i>
                  <h3 style={styles.cardTitle}>Mastery Level</h3>
                </div>
                <div style={styles.cardContent}>
                  <div style={styles.progressCircle}>
                    <div style={{
                      ...styles.progressRing,
                      background: `conic-gradient(${getMasteryColor(masteryPercentage)} ${masteryPercentage * 3.6}deg, #e5e7eb 0deg)`
                    }}>
                      <div style={styles.progressInner}>
                        <div style={styles.progressPercentage}>{formatPercentage(masteryPercentage)}%</div>
                        <div style={styles.progressLabel}>Mastered</div>
                      </div>
                    </div>
                  </div>
                  <div style={styles.progressStats}>
                    <div style={styles.statItem}>
                      <i className="fas fa-crown" style={{color: '#10b981'}}></i>
                      <span style={styles.statText}>{progressData.mastery_breakdown.mastered} Mastered</span>
                    </div>
                    <div style={styles.statItem}>
                      <i className="fas fa-clock" style={{color: '#f59e0b'}}></i>
                      <span style={styles.statText}>{progressData.mastery_breakdown.almost_there} Almost There</span>
                    </div>
                    <div style={styles.statItem}>
                      <i className="fas fa-exclamation-triangle" style={{color: '#ef4444'}}></i>
                      <span style={styles.statText}>{progressData.mastery_breakdown.needs_work} Needs Work</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Detailed Statistics */}
            <div style={styles.detailedSection}>
              <h2 style={styles.sectionTitle}>Detailed Breakdown</h2>
              
              <div style={styles.statsGrid}>
                <div style={styles.statCard}>
                  <div style={styles.statCardHeader}>
                    <i className="fas fa-check-double" style={{...styles.statCardIcon, color: '#10b981'}}></i>
                    <h4 style={styles.statCardTitle}>Completion Rate</h4>
                  </div>
                  <div style={styles.statCardValue}>{formatPercentage(completionPercentage)}%</div>
                  <div style={styles.statCardDescription}>
                    {progressData.answered} of {progressData.total_questions} questions answered
                  </div>
                  <div style={styles.progressBar}>
                    <div 
                      style={{
                        ...styles.progressFill,
                        width: `${completionPercentage}%`,
                        backgroundColor: getCompletionColor(completionPercentage)
                      }}
                    ></div>
                  </div>
                </div>

                <div style={styles.statCard}>
                  <div style={styles.statCardHeader}>
                    <i className="fas fa-brain" style={{...styles.statCardIcon, color: '#8b5cf6'}}></i>
                    <h4 style={styles.statCardTitle}>Mastery Rate</h4>
                  </div>
                  <div style={styles.statCardValue}>{formatPercentage(masteryPercentage)}%</div>
                  <div style={styles.statCardDescription}>
                    {progressData.mastery_breakdown.mastered} of {progressData.answered} answered correctly
                  </div>
                  <div style={styles.progressBar}>
                    <div 
                      style={{
                        ...styles.progressFill,
                        width: `${masteryPercentage}%`,
                        backgroundColor: getMasteryColor(masteryPercentage)
                      }}
                    ></div>
                  </div>
                </div>

                <div style={styles.statCard}>
                  <div style={styles.statCardHeader}>
                    <i className="fas fa-bullseye" style={{...styles.statCardIcon, color: '#f59e0b'}}></i>
                    <h4 style={styles.statCardTitle}>Accuracy</h4>
                  </div>
                  <div style={styles.statCardValue}>
                    {progressData.answered > 0 
                      ? formatPercentage(getMasteryCategoryPercentage(progressData.mastery_breakdown.mastered))
                      : '0.00'}%
                  </div>
                  <div style={styles.statCardDescription}>
                    Based on your answered questions
                  </div>
                </div>

                <div style={styles.statCard}>
                  <div style={styles.statCardHeader}>
                    <i className="fas fa-chart-pie" style={{...styles.statCardIcon, color: '#ef4444'}}></i>
                    <h4 style={styles.statCardTitle}>Remaining</h4>
                  </div>
                  <div style={styles.statCardValue}>{progressData.unanswered}</div>
                  <div style={styles.statCardDescription}>
                    Questions left to complete
                  </div>
                </div>
              </div>
            </div>

            {/* Mastery Breakdown */}
            <div style={styles.masterySection}>
              <h2 style={styles.sectionTitle}>Mastery Breakdown</h2>
              
              <div style={styles.masteryGrid}>
                <div style={styles.masteryItem}>
                  <div style={styles.masteryHeader}>
                    <div style={{...styles.masteryIcon, backgroundColor: '#ef4444'}}>
                      <i className="fas fa-exclamation-triangle" style={{color: 'white', fontSize: '20px'}}></i>
                    </div>
                    <div style={styles.masteryInfo}>
                      <div style={styles.masteryName}>Needs Work</div>
                      <div style={styles.masteryDescription}>Questions answered incorrectly</div>
                    </div>
                  </div>
                  <div style={styles.masteryStats}>
                    <div style={styles.masteryCount}>{progressData.mastery_breakdown.needs_work}</div>
                    <div style={styles.masteryPercentage}>
                      ({formatPercentage(getMasteryCategoryPercentage(progressData.mastery_breakdown.needs_work))}%)
                    </div>
                  </div>
                </div>

                <div style={styles.masteryItem}>
                  <div style={styles.masteryHeader}>
                    <div style={{...styles.masteryIcon, backgroundColor: '#f59e0b'}}>
                      <i className="fas fa-clock" style={{color: 'white', fontSize: '20px'}}></i>
                    </div>
                    <div style={styles.masteryInfo}>
                      <div style={styles.masteryName}>Almost There</div>
                      <div style={styles.masteryDescription}>Questions with partial understanding</div>
                    </div>
                  </div>
                  <div style={styles.masteryStats}>
                    <div style={styles.masteryCount}>{progressData.mastery_breakdown.almost_there}</div>
                    <div style={styles.masteryPercentage}>
                      ({formatPercentage(getMasteryCategoryPercentage(progressData.mastery_breakdown.almost_there))}%)
                    </div>
                  </div>
                </div>

                <div style={styles.masteryItem}>
                  <div style={styles.masteryHeader}>
                    <div style={{...styles.masteryIcon, backgroundColor: '#10b981'}}>
                      <i className="fas fa-crown" style={{color: 'white', fontSize: '20px'}}></i>
                    </div>
                    <div style={styles.masteryInfo}>
                      <div style={styles.masteryName}>Mastered</div>
                      <div style={styles.masteryDescription}>Questions answered correctly</div>
                    </div>
                  </div>
                  <div style={styles.masteryStats}>
                    <div style={styles.masteryCount}>{progressData.mastery_breakdown.mastered}</div>
                    <div style={styles.masteryPercentage}>
                      ({formatPercentage(getMasteryCategoryPercentage(progressData.mastery_breakdown.mastered))}%)
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div style={styles.actionsSection}>
              <h2 style={styles.sectionTitle}>Quick Actions</h2>
              
              <div style={styles.actionsGrid}>
                <button 
                  style={styles.actionButton} 
                  onClick={handleContinueStudying}
                >
                  <i className="fas fa-play" style={styles.actionIcon}></i>
                  <span style={styles.actionText}>Continue Studying</span>
                  <span style={styles.actionSubtext}>Pick up where you left off</span>
                </button>
                
                <button 
                  style={styles.actionButton} 
                  onClick={handleFocusWeakAreas}
                >
                  <i className="fas fa-bullseye" style={styles.actionIcon}></i>
                  <span style={styles.actionText}>Focus on Weak Areas</span>
                  <span style={styles.actionSubtext}>Practice questions that need work</span>
                </button>
                
                <button style={styles.actionButton}>
                  <i className="fas fa-chart-line" style={styles.actionIcon}></i>
                  <span style={styles.actionText}>View Detailed Reports</span>
                  <span style={styles.actionSubtext}>See topic-wise performance</span>
                </button>
                
                <button style={styles.actionButton} onClick={fetchProgressData}>
                  <i className="fas fa-redo" style={styles.actionIcon}></i>
                  <span style={styles.actionText}>Refresh Data</span>
                  <span style={styles.actionSubtext}>Update progress information</span>
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        select:focus {
          outline: none;
          border-color: #4f46e5;
          box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.1);
        }

        button:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }
      `}</style>
    </div>
  );
};

const styles = {
  container: {
    backgroundColor: '#f8f9fa',
    minHeight: '100vh',
    padding: '20px',
    fontFamily: '"Inter", sans-serif',
  },
  header: {
    backgroundColor: '#ffffff',
    borderRadius: '10px',
    padding: '30px',
    marginBottom: '25px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
    border: '1px solid #e0e0e0'
  },
  headerContent: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    maxWidth: '1200px',
    margin: '0 auto',
    flexWrap: 'wrap',
    gap: '20px'
  },
  titleSection: {
    flex: 1
  },
  title: {
    fontSize: '32px',
    fontWeight: '600',
    margin: '0 0 8px 0',
    color: '#2c5aa0',
    fontFamily: '"Inter", sans-serif',
    letterSpacing: '0px',
    lineHeight: '1.2'
  },
  subtitle: {
    fontSize: '16px',
    margin: '0',
    color: '#666666',
    fontFamily: '"Inter", sans-serif',
    letterSpacing: '0.25px',
    lineHeight: '1.5'
  },
  topicSelector: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px'
  },
  selectorLabel: {
    fontSize: '14px',
    fontWeight: '500',
    color: '#374151',
    display: 'flex',
    alignItems: 'center'
  },
  selector: {
    padding: '8px 12px',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    fontSize: '14px',
    color: '#374151',
    backgroundColor: '#ffffff',
    cursor: 'pointer',
    minWidth: '200px'
  },
  divider: {
    height: '1px',
    backgroundColor: '#e0e0e0',
    margin: '20px 0',
    maxWidth: '1200px',
    marginLeft: 'auto',
    marginRight: 'auto'
  },
  content: {
    maxWidth: '1200px',
    margin: '0 auto 30px auto'
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '60px 20px',
    textAlign: 'center'
  },
  spinner: {
    width: '50px',
    height: '50px',
    border: '4px solid #f3f3f3',
    borderTop: '4px solid #2c5aa0',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
    marginBottom: '20px'
  },
  loadingText: {
    fontSize: '16px',
    color: '#666666',
    fontWeight: '500'
  },
  errorContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '60px 20px',
    textAlign: 'center'
  },
  errorIcon: {
    marginBottom: '20px'
  },
  errorTitle: {
    fontSize: '20px',
    fontWeight: '600',
    color: '#111827',
    margin: '0 0 12px 0'
  },
  errorText: {
    fontSize: '14px',
    color: '#6b7280',
    margin: '0 0 24px 0',
    maxWidth: '400px'
  },
  retryButton: {
    backgroundColor: '#4f46e5',
    border: 'none',
    borderRadius: '6px',
    padding: '10px 20px',
    color: '#ffffff',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    transition: 'background-color 0.2s ease'
  },
  overviewGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
    gap: '24px',
    marginBottom: '32px'
  },
  overviewCard: {
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    padding: '24px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
    border: '1px solid #e0e0e0'
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '20px'
  },
  cardIcon: {
    fontSize: '24px',
    color: '#4f46e5'
  },
  cardTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#111827',
    margin: 0
  },
  cardContent: {
    display: 'flex',
    alignItems: 'center',
    gap: '32px'
  },
  progressCircle: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  progressRing: {
    width: '120px',
    height: '120px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  progressInner: {
    width: '100px',
    height: '100px',
    borderRadius: '50%',
    backgroundColor: '#ffffff',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center'
  },
  progressPercentage: {
    fontSize: '20px',
    fontWeight: '700',
    color: '#111827'
  },
  progressLabel: {
    fontSize: '12px',
    color: '#6b7280',
    fontWeight: '500'
  },
  progressStats: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '12px'
  },
  statItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  statText: {
    fontSize: '14px',
    color: '#374151',
    fontWeight: '500'
  },
  detailedSection: {
    marginBottom: '32px'
  },
  sectionTitle: {
    fontSize: '24px',
    fontWeight: '600',
    color: '#111827',
    margin: '0 0 20px 0'
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '20px'
  },
  statCard: {
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    padding: '24px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
    border: '1px solid #e0e0e0'
  },
  statCardHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '16px'
  },
  statCardIcon: {
    fontSize: '20px'
  },
  statCardTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#374151',
    margin: 0
  },
  statCardValue: {
    fontSize: '32px',
    fontWeight: '700',
    color: '#111827',
    margin: '0 0 8px 0'
  },
  statCardDescription: {
    fontSize: '14px',
    color: '#6b7280',
    margin: '0 0 16px 0'
  },
  progressBar: {
    height: '6px',
    backgroundColor: '#e5e7eb',
    borderRadius: '3px',
    overflow: 'hidden'
  },
  progressFill: {
    height: '100%',
    borderRadius: '3px',
    transition: 'width 0.3s ease'
  },
  masterySection: {
    marginBottom: '32px'
  },
  masteryGrid: {
    display: 'grid',
    gap: '16px'
  },
  masteryItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px',
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
    border: '1px solid #e0e0e0'
  },
  masteryHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px'
  },
  masteryIcon: {
    width: '48px',
    height: '48px',
    borderRadius: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  masteryInfo: {
    display: 'flex',
    flexDirection: 'column'
  },
  masteryName: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#111827',
    margin: '0 0 4px 0'
  },
  masteryDescription: {
    fontSize: '14px',
    color: '#6b7280',
    margin: 0
  },
  masteryStats: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end'
  },
  masteryCount: {
    fontSize: '24px',
    fontWeight: '700',
    color: '#111827',
    margin: '0 0 4px 0'
  },
  masteryPercentage: {
    fontSize: '14px',
    color: '#6b7280'
  },
  actionsSection: {
    marginBottom: '32px'
  },
  actionsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '20px'
  },
  actionButton: {
    backgroundColor: '#ffffff',
    border: '1px solid #e5e7eb',
    borderRadius: '12px',
    padding: '24px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    textAlign: 'left'
  },
  actionIcon: {
    fontSize: '24px',
    color: '#4f46e5',
    marginBottom: '12px'
  },
  actionText: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#111827',
    margin: '0 0 4px 0'
  },
  actionSubtext: {
    fontSize: '14px',
    color: '#6b7280',
    margin: 0
  }
};

export default Progress;